import { apiBaseAbsolute } from "../../../lib/api-base";

/**
 * Shared conversations.
 *
 * A chat is saved on the account (`concierge_sessions`) and can be handed out
 * as a link. Two audiences, two shapes of call:
 *
 *  · the OWNER mints and revokes the link — `credentials: "include"`, because
 *    these are their sessions;
 *  · the HOLDER reads it with no account at all. The token is the whole
 *    authorisation, which is why it is 32 random bytes rather than a short id
 *    like Mindtrip's `/chat/9894196`.
 *
 * Nothing here throws on a missing session: a revoked or mistyped link is an
 * ordinary outcome, not an error, and the page shows "this link is no longer
 * available" rather than a crash.
 */

export interface SharedChatTurn {
  role: "user" | "assistant";
  content: string;
  slugs?: string[];
}

/**
 * A venue named by one of the turns, resolved to card data by the API.
 *
 * Sent with the transcript rather than looked up here: the share page renders
 * on the server, so the cards have to be in the first HTML. Resolved live, so
 * a row the catalogue has since pulled is simply absent and the prose that
 * mentions it stands on its own.
 */
export interface SharedChatCard {
  slug: string;
  title: string;
  subtitle: string | null;
  heroImageUrl: string | null;
  rating: number | null;
  citySlug: string | null;
  cityName: string | null;
  categorySlug: string | null;
  categoryName: string | null;
  segment: string | null;
  mode: "bookable" | "explore" | null;
  priceMad: number | null;
}

export interface SharedChat {
  title: string | null;
  city: string | null;
  lang: string;
  sharedAt: string | null;
  turns: SharedChatTurn[];
  /** Keyed by slug at render time; may be shorter than the union of `slugs`. */
  cards?: SharedChatCard[];
}

/**
 * Saves the conversation onto the account, creating the session on first call.
 * Returns the session id to keep appending to — pass it back next time.
 *
 * Signed-out guests never reach this: the route requires a session, and the
 * schema is accounts-only by owner decision (there is no device id column and
 * never should be).
 */
export async function checkpointChat(input: {
  sessionId?: string;
  turns: { role: "user" | "assistant"; content: string; slugs?: string[] }[];
}): Promise<string | null> {
  const res = await fetch(`${apiBaseAbsolute()}/ai/recaps/checkpoint`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) return null;
  const json = (await res.json()) as { sessionId?: string };
  return json.sessionId ?? null;
}

/** Mints the link, or returns the one already minted for this session. */
export async function shareChat(sessionId: string): Promise<string | null> {
  const res = await fetch(`${apiBaseAbsolute()}/ai/recaps/${sessionId}/share`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) return null;
  const json = (await res.json()) as { token?: string };
  return json.token ?? null;
}

/** Revokes it. The link stops working and retention applies to the row again. */
export async function unshareChat(sessionId: string): Promise<boolean> {
  const res = await fetch(`${apiBaseAbsolute()}/ai/recaps/${sessionId}/share`, {
    method: "DELETE",
    credentials: "include",
  });
  return res.ok;
}

/** The public read. Runs on the server too, so the transcript is in the HTML. */
export async function fetchSharedChat(token: string): Promise<SharedChat | null> {
  const res = await fetch(`${apiBaseAbsolute()}/ai/shared/${encodeURIComponent(token)}`);
  if (!res.ok) return null;
  return (await res.json()) as SharedChat;
}

/** Copies a shared conversation into one of the reader's own. Needs an account. */
export async function forkSharedChat(token: string): Promise<string | null> {
  const res = await fetch(
    `${apiBaseAbsolute()}/ai/shared/${encodeURIComponent(token)}/fork`,
    { method: "POST", credentials: "include" },
  );
  if (!res.ok) return null;
  const json = (await res.json()) as { sessionId?: string };
  return json.sessionId ?? null;
}

/**
 * Reopens a saved conversation: the recent turns, as text.
 *
 * Cards are not carried — `resume` returns roles and content only, which is
 * what the phone reads too. A resumed thread therefore reads back as prose
 * and regains its cards from the next answer onward.
 */
export async function resumeChat(
  sessionId: string,
): Promise<{ role: "user" | "assistant"; content: string }[] | null> {
  const res = await fetch(`${apiBaseAbsolute()}/ai/recaps/${sessionId}/resume`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) return null;
  const json = (await res.json()) as {
    turns?: { role: "user" | "assistant"; content: string }[];
  };
  return json.turns ?? null;
}

/** The URL an owner copies. Absolute, because it is going into a message. */
export function sharedChatUrl(token: string, lang: string): string {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://majourneys.com";
  return `${origin}/${lang}/chat/${token}`;
}
